// Typed JSON-LD builders for SEO. Uses schema-dts for type safety.
import type {
  Organization,
  ProfessionalService,
  WebSite,
  Service,
  BreadcrumbList,
  FAQPage,
  Person,
  ContactPage,
  AboutPage,
  WithContext,
  Thing,
} from "schema-dts";

export const SITE_URL = "https://vert-ai.com";
export const SITE_NAME = "Vert AI";
export const FOUNDER_NAME = "Guillaume Belisle";
export const FOUNDER_EMAIL = "guillaume.belisle@vert-ai.com";
export const LINKEDIN_URL = "https://www.linkedin.com/company/vert-ai";

export const organizationJsonLd = (): WithContext<Organization> => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/og-image.png`,
  sameAs: [LINKEDIN_URL],
  founder: {
    "@type": "Person",
    name: FOUNDER_NAME,
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      email: FOUNDER_EMAIL,
      contactType: "customer support",
      areaServed: ["CA", "US"],
      availableLanguage: ["English", "French"],
    },
  ],
  address: {
    "@type": "PostalAddress",
    addressCountry: "CA",
  },
  areaServed: ["CA", "US"],
});

export const websiteJsonLd = (): WithContext<WebSite> => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  inLanguage: ["en-CA", "fr-CA"],
  publisher: { "@id": `${SITE_URL}/#organization` },
});

export const professionalServiceJsonLd = (): WithContext<ProfessionalService> => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#service`,
  name: SITE_NAME,
  url: SITE_URL,
  image: `${SITE_URL}/og-image.png`,
  description:
    "Production-grade AI systems, data platforms, and decision science. One team, scope to production.",
  priceRange: "$$$",
  address: {
    "@type": "PostalAddress",
    addressCountry: "CA",
  },
  areaServed: ["CA", "US"],
});

export const breadcrumbJsonLd = (
  items: Array<{ name: string; path: string }>
): WithContext<BreadcrumbList> => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path === "/" ? "" : item.path}`,
  })),
});

export const serviceJsonLd = (params: {
  name: string;
  description: string;
  url: string;
  serviceType?: string;
  audience?: string;
}): WithContext<Service> => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: params.name,
  description: params.description,
  url: params.url,
  serviceType: params.serviceType ?? params.name,
  provider: { "@id": `${SITE_URL}/#organization` },
  areaServed: ["CA", "US"],
  ...(params.audience
    ? {
        audience: {
          "@type": "Audience",
          audienceType: params.audience,
        },
      }
    : {}),
});

export const faqPageJsonLd = (
  items: Array<{ q: string; a: string }>
): WithContext<FAQPage> => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
});

export const personJsonLd = (params: {
  name: string;
  jobTitle: string;
  url: string;
  sameAs?: string[];
}): WithContext<Person> => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: params.name,
  jobTitle: params.jobTitle,
  url: params.url,
  worksFor: { "@id": `${SITE_URL}/#organization` },
  ...(params.sameAs ? { sameAs: params.sameAs } : {}),
});

export const contactPageJsonLd = (params: {
  url: string;
  name: string;
  description: string;
}): WithContext<ContactPage> => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: params.url,
  name: params.name,
  description: params.description,
  mainEntity: { "@id": `${SITE_URL}/#organization` },
});

export const aboutPageJsonLd = (params: {
  url: string;
  name: string;
  description: string;
}): WithContext<AboutPage> => ({
  "@context": "https://schema.org",
  "@type": "AboutPage",
  url: params.url,
  name: params.name,
  description: params.description,
  mainEntity: { "@id": `${SITE_URL}/#organization` },
});

const MONTH_MAP: Record<string, string> = {
  january: "01", february: "02", march: "03", april: "04",
  may: "05", june: "06", july: "07", august: "08",
  september: "09", october: "10", november: "11", december: "12",
  janvier: "01", février: "02", mars: "03", avril: "04",
  mai: "05", juin: "06", juillet: "07", août: "08",
  septembre: "09", octobre: "10", novembre: "11", décembre: "12",
};

export function parseDisplayDate(display: string): string {
  const parts = display.toLowerCase().trim().split(/\s+/);
  if (parts.length >= 2) {
    const month = MONTH_MAP[parts[0]] || "01";
    const year = parts[1];
    return `${year}-${month}-01`;
  }
  return "2026-01-01";
}

export function parseReadMinutes(readTime: string): string {
  const match = readTime.match(/(\d+)/);
  const mins = match ? parseInt(match[1], 10) : 8;
  return `PT${mins}M`;
}

export const articleJsonLd = (params: {
  headline: string;
  description: string;
  datePublished: string; // display string like "March 2026"
  readTime: string;
  category: string;
  slug: string;
  locale: string;
  wordCount: number;
}): WithContext<Thing> => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: params.headline,
  description: params.description,
  datePublished: parseDisplayDate(params.datePublished),
  dateModified: parseDisplayDate(params.datePublished),
  author: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
  },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/og-image.png`,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE_URL}/insights/${params.slug}`,
  },
  articleSection: params.category,
  inLanguage: params.locale === "fr" ? "fr-CA" : "en-CA",
  wordCount: params.wordCount,
  timeRequired: parseReadMinutes(params.readTime),
}) as WithContext<Thing>;
